<template>
	<div class="card">
		<div class="card-header border-bottom align-items-center d-flex justify-content-between pr-3">
			<div>
				<span class="header-title">{{ __('Suggestions') }}</span>
				<span class="badge brand-primary-badge">{{ insightsCount }}</span>
			</div>

			<div class="positive-negative-filter">
	            <a href="#" @click.prevent="setKind('negative')" :class="{active: kind == 'negative'}">
	                <strong class="light-text">{{ __('NEGATIVE') }}</strong>
	            </a>
	            <a href="#" @click.prevent="setKind('positive')" :class="{active: kind == 'positive'}">
	                <strong class="light-text">{{ __('POSITIVE') }}</strong>
	            </a>
	        </div>

		</div>
		<div class="card-body" :class="{loading: activeInsights === null}">

			<div class="loader">
				<i class="fas fa-circle-notch fa-spin"></i> Fetching the latest insights..
			</div>

			<span v-if="activeInsights != null && activeInsights.length == 0">
				There are currently no insights/suggestions.
			</span>

			<insight v-for="insight in activeInsights" :insight="insight"
				:key="insight.id"
				inline-template>
				<div class="insight border">
					
					<div class="insight-legend border">
						<span class="lil-face" :class="insight.kind == 'negative' ? 'text-danger' : 'text-success'">
							<i class="far" :class="insight.kind == 'negative' ? 'fa-frown' : 'fa-smile'"></i>
						</span>
						<span class="legend">{{ insight.term_category }}</span>
					</div>
					
					<div class="insight-body">
						<div class="d-flex flex-wrap">
							<div class="element small-element">
								<p class="title">TOPIC</p>
								<h4 class="text blue-text">{{ ucFirst(insight.term_keyword) }}</h4>
							</div>
							<div class="element small-element">
								<div v-if="insight.kind=='negative'">
									<p class="title">IMPACT 
										<i class="far fa-question-circle" data-toggle="tooltip" data-placement="top" :title="impactExplanation"></i>
									</p>
									<impact-details
										:impacts="insight.impacts"
										:insight_kind="insight.kind">
									</impact-details>
								</div>
							</div>
							<div class="negative-mention big-element">
								<p class="title">{{ kindTitle }} MENTION</p>
								<p class="quote readable-text">
									<span v-html="quoteText"></span>
									<a href="#" class="blue-link" v-if="insight.quote != ''" @click.prevent="seeReviews">See more</a>
								</p>
							</div>
							<div class="element small-element">
								<p class="title">WEIGHT <i class="far fa-question-circle" data-toggle="tooltip" data-placement="top" :title="weightExplanation"></i></p>
								<h3 class="text">{{ insightWeight }}%</h3>
							</div>
							<div class="element small-element">
								<p class="title">NUMBER OF MENTIONS</p>
								<h3 class="text">{{ numMentions }}</h3>
							</div>
							<div class="details big-element">
								<div class="d-flex metrics">
									<div class="element last-seen">
										<p class="title">ISSUE SPOTTED LAST</p>
										<p>{{ periodLabels }} <a href="#" @click.prevent="viewPeriodBreakdown()" class="blue-link">View Breakdown</a></p>
									</div>
									<div class="element">
										<p class="title">METRICS</p>
										<a href="#" @click.prevent="viewTrendsChart()" class="blue-link">
											<img src="/assets/img/1.0/suggestions-active.png" class="active"> VIEW TRENDS
										</a>
									</div>
								</div>
							</div>
						</div>
					</div>

					<div class="insight-actions" :class="{show: showActions}">
						<div class="collapsed">
							<button class="btn btn-primary btn-actions" @click="showActions = true">Actions <i class="fas fa-ellipsis-h"></i></button>
						</div>
						<div class="expanded">
							<div class="action">SEE TRENDS <i class="fas fa-chart-line"></i></div>
							<div class="action">SHARE <i class="fas fa-share"></i></div>
							<button class="btn btn-primary btn-close" @click="showActions = false"><i class="fa fa-times"></i></button>
						</div>
					</div>

				</div>				
			</insight>

		</div>
		
		<!-- Modal -->
		<div class="modal fade insight-trends-modal" ref="trends_modal" aria-label="Review Trends" tabindex="-1" role="dialog" aria-hidden="true">
		  <div class="modal-dialog gf modal-dialog-centered" role="document">
		    <div class="modal-content">
		    	<div class="modal-header">
			        <h5 class="modal-title" id="trendsModalLabel">
			        	<i class="far" :class="smileyFromInsight(activeTrendInsight)"></i> {{ activeTrendInsight.term_category }}
		        	</h5>
			        <button type="button" class="close" data-dismiss="modal" aria-label="Close">
			          <span aria-hidden="true"><i class="fa fa-times"></i></span>
			        </button>
		        </div>
		        <div class="modal-body">
		        	<div class="d-flex justify-content-between synopsis">
		        		<div class="progress-for">
		        			<strong class="header-case">PROGRESS FOR</strong>
		        			<span class="blue-text">{{ activeTrendInsight.term_keyword ? ucFirst(activeTrendInsight.term_keyword): '' }}</span>
		        		</div>

		        		<div class="impact-breakdown" v-if="activeTrendInsight.kind=='negative'">
		        			<span class="title">IMPACT</span>
		        			<impact-details
		        				:impacts="activeTrendInsight.impacts"
		        				:insight_kind="activeTrendInsight.kind">
		        			</impact-details>
		        		</div>
						
						<div class="reviews-stats">
		        			<i class="fa fa-comment"></i>
		        			Mentions
		        			<span class="blue-text">{{ numberOfReviewsFromInsight(activeTrendInsight) }}</span>
		        		</div>

		        	</div>
		        	<insight-trends-chart :trends="insightTrends" :responsive="false" type="all"></insight-trends-chart>
		        </div>
		    </div>
		  </div>
		</div>

		<div class="modal fade period-breakdown-modal" ref="period_breakdown_modal" aria-label="Insight Periods" tabindex="-1" role="dialog" aria-hidden="true">
		  <div class="modal-dialog gf modal-dialog-centered" role="document">
		    <div class="modal-content">
		    	<div class="modal-header">
			        <h5 class="modal-title" id="trendsModalLabel">
			        	<i class="far" :class="smileyFromInsight(breakdownInsight)"></i> {{ breakdownInsight.term_category }}
		        	</h5>
			        <button type="button" class="close" data-dismiss="modal" aria-label="Close">
			          <span aria-hidden="true"><i class="fa fa-times"></i></span>
			        </button>
		        </div>
		        <div class="modal-body">
		        	<div class="d-flex justify-content-between synopsis">
		        		<div class="progress-for">
		        			<strong class="header-case">BREAKDOWN FOR</strong>
		        			<span class="blue-text">{{ breakdownInsight.term_keyword ? ucFirst(breakdownInsight.term_keyword): '' }}</span>
		        		</div>

		        		<div class="impact-breakdown" v-if="breakdownInsight.kind=='negative'">
		        			<span class="title">IMPACT</span>
		        			<impact-details
		        				:impacts="breakdownInsight.impacts"
		        				:insight_kind="breakdownInsight.kind">
		        			</impact-details>
		        		</div>
						
						<div class="reviews-stats">
		        			<i class="fa fa-comment"></i>
		        			Mentions
		        			<span class="blue-text">{{ numberOfReviewsFromInsight(breakdownInsight) }}</span>
		        		</div>

		        	</div>
		        	<insight-periods-breakdown :insight="breakdownInsight"></insight-periods-breakdown>
		        </div>
		    </div>
		  </div>
		</div>

	</div>
</template>
<script>
	export default {
		props: ['user', 'teams'],
		data() {
			return {
				activeInsights: null,
				hasError: false,
				kind: 'negative',
				insights: {
					positive: null,
					negative: null
				},
				insightTrends: null,
				activeTrendInsight: {},
				breakdownInsight: {}
			}
		},
		computed: {
			insightsCount() {
				return this.activeInsights != null ? this.activeInsights.length : 0
			}
		},
		mounted() {

			this.$on('showTrendModal', insight => this.updateAndOpenChart(insight))
			this.$on('showPeriodModal', insight => this.showPeriodBreakdown(insight))
			this.updatePage();
				
		},
		methods: {
			updatePage() {
				this.updateTeamInsights()
			},
			smileyFromInsight(insight) {
				return insight.kind == 'positive' ? 
					'fa-smile text-success' : 'fa-frown text-danger'
			},
			numberOfReviewsFromInsight(insight) {
				try {
					return insight.period_stats[insight.period_stats.length - 1].count_number
				} catch(err) {
					return 0
				}
			},
			showPeriodBreakdown(insight) {
				
				this.breakdownInsight = insight
				$(this.$refs.period_breakdown_modal).modal('toggle')

			},
			updateAndOpenChart(insight) {

				this.activeTrendInsight = insight

				let url = '/api/insights/' + insight.team_id + '/all/trend?keyword=' + insight.term_keyword
					+ '&category=' + encodeURIComponent(insight.term_category)


				axios.get(url).then(response => {

					this.insightTrends = response.data

				}).catch(error => {
				})

				$(this.$refs.trends_modal).modal('toggle')
			},
			updateTeamInsights() {

				axios.get('/api/insights/' + Spark.state.currentTeam.id + '/'
				  + this.kind + '/rolling')
				.then(response => {

	                this.insights[this.kind] = response.data
	                this.activeInsights = this.insights[this.kind]

	            }).catch(error => {

	            	this.hasError = true
	                this.teamInsights = []

	            });
			},

			setKind(kind) {

	      analytics.track("Set Insight Type", {
	        type: kind
	      })

				this.kind = kind

				if (this.insights[kind]) {
					this.activeInsights = this.insights[kind];
				} else {
					this.activeInsights = null
					this.updateTeamInsights()
				}

			}

		}
	}
</script>